import React, { useState, useEffect } from 'react';
import { X, Search, Command, Calendar, Users, FileText, Settings, Home, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const KeyboardShortcuts = ({ onNavigate, onClose }) => {
  const shortcuts = [
    { key: 'Ctrl/⌘ + K', action: 'Open command palette', icon: Command },
    { key: 'Ctrl/⌘ + /', action: 'Show keyboard shortcuts', icon: Command },
    { key: 'Ctrl/⌘ + H', action: 'Go to Dashboard', icon: Home },
    { key: 'Ctrl/⌘ + C', action: 'Go to Calendar', icon: Calendar },
    { key: 'Ctrl/⌘ + P', action: 'Go to Clients', icon: Users },
    { key: 'Ctrl/⌘ + D', action: 'Go to Documents', icon: FileText },
    { key: 'Ctrl/⌘ + A', action: 'Go to Administration', icon: Settings },
    { key: 'Ctrl/⌘ + S', action: 'Search', icon: Search },
    { key: 'Esc', action: 'Close modals', icon: X },
    { key: '?', action: 'Show help', icon: Command }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Command className="w-5 h-5" />
              Keyboard Shortcuts
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shortcuts.map((shortcut, index) => {
              const Icon = shortcut.icon;
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm text-gray-700">{shortcut.action}</span>
                  </div>
                  <kbd className="px-3 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-600">
                    {shortcut.key}
                  </kbd>
                </div>
              );
            })}
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Press <kbd className="px-2 py-1 bg-white border border-blue-300 rounded text-xs font-mono mx-1">Ctrl/⌘ + K</kbd> anytime to quickly search and navigate
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Command Palette Component
export const CommandPalette = ({ onNavigate, onClose, clients = [], toast }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const commands = [
    { id: 'dashboard', name: 'Go to Home', icon: Home, action: () => onNavigate('home') },
    { id: 'calendar', name: 'Go to Calendar', icon: Calendar, action: () => onNavigate('calendar') },
    { id: 'clients', name: 'View My Clients', icon: Users, action: () => onNavigate('clinical') },
    { id: 'documents', name: 'Go to Documents', icon: FileText, action: () => onNavigate('administration', 'documents') },
    { id: 'admin', name: 'Go to Administration', icon: Settings, action: () => onNavigate('administration') },
    { id: 'analytics', name: 'View Analytics', icon: Clock, action: () => onNavigate('administration', 'analytics') },
    ...clients.slice(0, 5).map(p => ({
      id: `client-${p.id}`,
      name: `View ${p.first_name} ${p.last_name}`,
      icon: Users,
      action: () => {
        toast.info(`Opening ${p.first_name} ${p.last_name}'s profile`);
        onClose();
      }
    }))
  ];

  const filteredCommands = commands.filter(cmd =>
    cmd.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
        e.preventDefault();
        filteredCommands[selectedIndex].action();
        onClose();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredCommands, selectedIndex, onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-20 z-[9999] p-4">
      <Card className="w-full max-w-2xl animate-slide-down shadow-2xl sm:rounded-lg rounded-none">
        <CardContent className="p-0">
          <div className="relative">
            <Search className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Type a command or search..."
              className="w-full pl-12 pr-4 py-4 text-lg border-b border-gray-200 focus:outline-none focus:border-blue-500"
              autoFocus
            />
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filteredCommands.length > 0 ? (
              filteredCommands.map((cmd, index) => {
                const Icon = cmd.icon;
                return (
                  <button
                    key={cmd.id}
                    onClick={() => {
                      cmd.action();
                      onClose();
                    }}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                      ${index === selectedIndex ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'}
                    `}
                  >
                    <div className={`p-2 rounded-lg ${index === selectedIndex ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-gray-900">{cmd.name}</span>
                  </button>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No commands found</p>
              </div>
            )}
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t text-xs text-gray-500 flex items-center justify-between">
            <div className="flex gap-4">
              <span>↑↓ Navigate</span>
              <span>↵ Select</span>
              <span>Esc Close</span>
            </div>
            <span>Powered by Keyboard Magic ✨</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to handle all keyboard shortcuts
export const useKeyboardShortcuts = (handlers) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + K - Command Palette
      if (modKey && e.key === 'k') {
        e.preventDefault();
        handlers.openCommandPalette?.();
      }
      // Ctrl/Cmd + / - Shortcuts Help
      else if (modKey && e.key === '/') {
        e.preventDefault();
        handlers.openShortcutsHelp?.();
      }
      // Ctrl/Cmd + H - Dashboard
      else if (modKey && e.key === 'h') {
        e.preventDefault();
        handlers.navigateToDashboard?.();
      }
      // Ctrl/Cmd + C - Calendar
      else if (modKey && e.key === 'c') {
        e.preventDefault();
        handlers.navigateToCalendar?.();
      }
      // Ctrl/Cmd + P - Clients
      else if (modKey && e.key === 'p') {
        e.preventDefault();
        handlers.navigateToPatients?.();
      }
      // Ctrl/Cmd + D - Documents
      else if (modKey && e.key === 'd') {
        e.preventDefault();
        handlers.navigateToDocuments?.();
      }
      // Ctrl/Cmd + A - Administration
      else if (modKey && e.key === 'a') {
        e.preventDefault();
        handlers.navigateToAdministration?.();
      }
      // Ctrl/Cmd + S - Search
      else if (modKey && e.key === 's') {
        e.preventDefault();
        handlers.focusSearch?.();
      }
      // ? - Help
      else if (e.key === '?' && !e.shiftKey) {
        handlers.openShortcutsHelp?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlers]);
};

export default KeyboardShortcuts;
